import { useState, type ReactNode } from "react";
import type { RoutingRule, RoutingTemplate } from "@/app/routing";
import { Button, Pill } from "@/ui";
import { matchLine } from "./routingRuleDraft";
import type { TargetNames } from "./targetDraft";

/** 模板行默认显示的规则条数，多出的折叠。 */
const PREVIEW = 3;

/** 摘要文字：通道组合名后的 * 对读屏隐藏，另给视觉隐藏的"（通道组合）"，含义不只靠星号。 */
function lineText(r: RoutingRule, names: TargetNames): { text: string; node: ReactNode } {
  const text = matchLine(r, names);
  return { text, node: r.channel_group_id ? <>{text.slice(0, -1)}<span aria-hidden="true">*</span><span className="ax-sr-only">（通道组合）</span></> : text };
}

/**
 * 账号绑定行里目标右侧的匹配摘要（#363）：规则行一行，超长省略并在 title 给全文；模板行按模板内优先级纵向逐条列出成员规则，超过 PREVIEW 条可展开。
 * 带 ax-edit-aside：列表变窄时落到下一行。
 * 停用的模板标"模板停用"并整体弱化；模板里引用的规则未加载时显示 #id。数据只来自已加载的规则、模板与名称。
 */
export function AccountBindingSummary({ rule, template, rules, names }: {
  rule?: RoutingRule; template?: RoutingTemplate; rules: RoutingRule[]; names: TargetNames;
}) {
  const [all, setAll] = useState(false);
  // 始终占住摘要这一列，未选目标时为空，后面的启用开关与操作仍在各自列上对齐。
  if (!template) {
    const line = rule && lineText(rule, names);
    return <div className="ax-edit-aside ax-edit-summary">{line && <div className="ax-edit-line" title={line.text}>{line.node}</div>}</div>;
  }
  const items = [...template.items].sort((a, b) => a.priority - b.priority), off = template.status === "disabled";
  return <div className={off ? "ax-edit-aside ax-edit-summary is-off" : "ax-edit-aside ax-edit-summary"}>
    {off && <div><Pill tone="warning">模板停用，匹配时跳过</Pill></div>}
    {items.length ? <ol aria-label={`模板 ${template.name} 的规则`}>
      {(all ? items : items.slice(0, PREVIEW)).map(it => { const r = rules.find(x => x.id === it.rule_id), line = r ? lineText(r, names) : { text: `规则 #${it.rule_id} 未加载`, node: `规则 #${it.rule_id} 未加载` };
        return <li key={it.id} className={r ? "ax-edit-line" : "ax-edit-line is-missing"} title={line.text}>{line.node}</li>; })}
    </ol> : <div>模板没有规则</div>}
    {items.length > PREVIEW && <div><Button variant="ghost" size="sm" onClick={() => setAll(!all)}>{all ? "收起" : `展开全部 ${items.length} 条`}</Button></div>}
  </div>;
}
